import { styled } from "styled-components";
import { Typography } from "components/typography";
import { defaultTheme } from "@mindlercare/ui-web/src/shared/constants";
import {
  Container,
  TemplateArea,
  TemplateItemBg,
  TemplateItem,
} from "../../../../booking/calendar/selectTemplateButton/selectTemplateButton.styles";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { useOnClickOutside } from "hooks";

import { useSetIcbtRequestFeedbackDateIgnored } from "api/useIcbtQueries";
import { useFlag } from "@mindlercare/unleash-client";
import { Icon, useSnackbar } from "@mindlercare/ui-web";

interface Props {
  feedbacks?: {
    feedback_id: string;
    feedback_updated_at: string;
    feedback_status: "draft" | "published";
    feedback_text: string;
    feedback_seen_at: string | null;
  }[];
  communication?: {
    request_id: string;
    feedback_date: string;
    feedback_date_ignored_at: string | null;
  };
  displayDropdownUpwards?: boolean;
  isFeedbackLocked?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  position: relative;
`;

// Dropdown area, positioned relative to the arrow icon
const IcbtFeedbackStateCellDropDownArea = styled(TemplateArea)`
  top: 20px;
`;

export const IcbtFeedbackStateCell = ({
  feedbacks,
  communication,
  displayDropdownUpwards,
  isFeedbackLocked,
}: Props) => {
  const { t } = useTranslation();
  const [popupVisible, setShowPopup] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Input args are immutable so if there is a change in the props we want to avoid a crash by doing ...
  const sortedFeedbacks = [...(feedbacks ?? [])]
    .filter((feedback) => !!feedback.feedback_text)
    .sort(
      (a, b) =>
        new Date(b.feedback_updated_at).getTime() -
        new Date(a.feedback_updated_at).getTime(),
    );
  const newestFeedback =
    sortedFeedbacks.length > 0 ? sortedFeedbacks[0] : undefined;
  const feedbackPublished = newestFeedback?.feedback_status === "published";
  const noFeedbackNeededText = t("NO_FEEDBACK_NEEDED");
  const feedbackSentAtLabel = feedbackPublished
    ? t("FEEDBACK_STATUS_SENT")
    : t("FEEDBACK_DRAFT_STATUS_SENT");

  const ignoreFeedbackDateFeatureEnabled = useFlag("NO_ICBT_FEEDBACK_NEEDED");

  const feedbackStateTextFromNewestFeedback = newestFeedback
    ? feedbackSentAtLabel
    : "--";

  const feedbackStateText =
    ignoreFeedbackDateFeatureEnabled && communication?.feedback_date_ignored_at
      ? noFeedbackNeededText
      : feedbackStateTextFromNewestFeedback;

  const canIgnoreFeedbackDate =
    !feedbackPublished && communication?.feedback_date_ignored_at === null;

  const canCeaseIgnoreFeedbackDate =
    !feedbackPublished &&
    communication &&
    communication.feedback_date_ignored_at !== null;

  const ceaseIgnoreFeedbackDateDropdownItemText = t("FEEDBACK_NEEDED");

  const menuItemsLength = [
    canIgnoreFeedbackDate,
    canCeaseIgnoreFeedbackDate,
  ].filter((menuItem) => menuItem === true).length;

  const showArrowDown = ignoreFeedbackDateFeatureEnabled && menuItemsLength > 0;

  const noFeedbackNeeded =
    ignoreFeedbackDateFeatureEnabled &&
    feedbackStateText === noFeedbackNeededText;

  const feedbackStateTextColor = noFeedbackNeeded
    ? "ACTION_BLUE"
    : "GRAY_BLACK";

  const { mutateAsync: toggleFeedbackDateIgnored } =
    useSetIcbtRequestFeedbackDateIgnored();

  const arrowDownRef = useRef(null);
  const popupRef = useRef(null);

  useOnClickOutside(popupRef, popupVisible, () => setShowPopup(false), [
    arrowDownRef,
  ]);

  const feedbackSeenAt = newestFeedback?.feedback_seen_at;
  const getFeedbackIcon = () => {
    if (noFeedbackNeeded)
      return (
        <Icon.NoFeedback
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.GRAY}
        />
      );
    if (feedbackPublished && feedbackSeenAt)
      return (
        <Icon.FeedbackCheckmark
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.SUCCESS}
        />
      );
    if (feedbackPublished)
      return (
        <Icon.FeedbackCheckmark
          width={defaultTheme.ICON_SIZE.STANDARD}
          color={defaultTheme.COLORS.ERROR}
        />
      );
    return (
      <Icon.Feedback
        width={defaultTheme.ICON_SIZE.STANDARD}
        color={defaultTheme.COLORS.GRAY}
      />
    );
  };

  return (
    <Wrapper>
      {feedbackPublished ? (
        // === Mode: Published Feedback
        <IconFeedbackRow>
          <IconColumnWrapper>{getFeedbackIcon()}</IconColumnWrapper>
          <FeedbackTextColumn>
            <Typography
              insensitive
              variant="body-small"
              color={feedbackStateTextColor}
            >
              {feedbackStateText}
            </Typography>
            <Typography insensitive variant="body-small" color="GRAY_DARK">
              {feedbackSeenAt
                ? t("TABLE_STATUS_READ")
                : t("TABLE_STATUS_NOT_READ")}
            </Typography>
          </FeedbackTextColumn>
        </IconFeedbackRow>
      ) : (
        // === Mode: Interactive Dropdown
        <IconFeedbackRow>
          {!isFeedbackLocked && getFeedbackIcon()}
          <Typography
            insensitive
            variant="body-small"
            color={feedbackStateTextColor}
            underline={feedbackStateTextColor === "ACTION_BLUE"}
          >
            {feedbackStateText}
          </Typography>

          {showArrowDown && (
            <div ref={arrowDownRef}>
              <Icon.Expand
                fill={defaultTheme.COLORS.LIGHT_GREY}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowPopup((prev) => !prev);
                }}
              />
            </div>
          )}
          {popupVisible && (
            <Container
              ref={popupRef}
              style={{
                position: "absolute",
                left: 202, // Corresponds to the min-width on Container
                // ensures the dropdown is always aligned to the left, preventing it from shifting based on the length of the feedback state text
                top: displayDropdownUpwards ? -60 : 0,
              }}
            >
              <IcbtFeedbackStateCellDropDownArea>
                {canIgnoreFeedbackDate && (
                  <TemplateItemBg>
                    <TemplateItem
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        toggleFeedbackDateIgnored({
                          mode: "IGNORE",
                          icbtRequestId: communication.request_id,
                        })
                          // mutateAsync already throws on error
                          .then(() => {
                            enqueueSnackbar({
                              text: t("IGNORED_ICBT_FEEDBACK_DATE"),
                              isError: false,
                            });
                            setShowPopup(false);
                          })
                          .catch(() => {
                            enqueueSnackbar({
                              text: t("FAILED_SAVE"),
                              isError: true,
                            });
                          });
                      }}
                    >
                      <Typography
                        insensitive
                        variant="body-x-small-bold"
                        color="GRAY_BLACK"
                      >
                        {noFeedbackNeededText}
                      </Typography>
                    </TemplateItem>
                  </TemplateItemBg>
                )}
                {canCeaseIgnoreFeedbackDate && (
                  <TemplateItemBg>
                    <TemplateItem
                      onClick={(event: React.MouseEvent) => {
                        event.stopPropagation();
                        toggleFeedbackDateIgnored({
                          mode: "CEASE_IGNORE",
                          icbtRequestId: communication.request_id,
                        })
                          // mutateAsync already throws on error
                          .then(() => {
                            enqueueSnackbar({
                              text: t("CEASE_IGNORE_ICBT_FEEDBACK_DATE"),
                              isError: false,
                            });
                            setShowPopup(false);
                          })
                          .catch(() =>
                            enqueueSnackbar({
                              text: t("FAILED_SAVE"),
                              isError: true,
                            }),
                          );
                      }}
                    >
                      <Typography
                        insensitive
                        variant="body-x-small-bold"
                        color="GRAY_BLACK"
                      >
                        {ceaseIgnoreFeedbackDateDropdownItemText}
                      </Typography>
                    </TemplateItem>
                  </TemplateItemBg>
                )}
              </IcbtFeedbackStateCellDropDownArea>
            </Container>
          )}
        </IconFeedbackRow>
      )}
    </Wrapper>
  );
};

const IconFeedbackRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.SPACING.SM};
`;

const FeedbackTextColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconColumnWrapper = styled.div`
  margin-top: 14px;
`;
